import React, { useState } from "react";
import { Card, Button, Form, Alert, Spinner, Tabs, Tab } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";
import * as XLSX from "xlsx";

export default function ImportMf() {
  const [activeTab, setActiveTab] = useState("mf"); // 'mf' or 'linii'
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError("");
      setSuccess("");
    }
  };

  const downloadTemplate = () => {
    let headers, filename;
    if (activeTab === "mf") {
      headers = [
        [
          "BXID", "BXNUMAR_INV", "BXDENUMIRE", "BXSTARE", "BXDATA1", "BXDATASCOS",
          "BXVALINV", "BXCODCLAS_ID", "TIP_ID", "BXANI", "BXDESCRIERE", "DATA_TERM_AM", "VALOARE_VANZARE"
        ],
        [
          1, "INV-001", "Laptop Lenovo T14", "buna", 45200, null,
          4500.00, "2.1.24.2", 1, 3, "Laptop programare", 46295, 0.00
        ]
      ];
      filename = "model_import_mijloace_fixe.xlsx";
    } else {
      headers = [
        ["BXID", "BXTIPOPMF_ID", "BXMF_ID", "BXDEBIT", "BXCREDIT", "BXAMLTRANS", "BXDATA"],
        [1, 1, 1, 125.00, 0.00, 125.00, 45230]
      ];
      filename = "model_import_linii_mijloace_fixe.xlsx";
    }

    const ws = XLSX.utils.aoa_to_sheet(headers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, filename);
  };

  const handleUpload = () => {
    if (!file) {
      setError("Te rog selecteaza un fisier Excel mai intai.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const bstr = e.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rawData = XLSX.utils.sheet_to_json(ws);

        // Normalize keys
        const cleanedData = rawData.map(row => {
          const newRow = {};
          Object.keys(row).forEach(key => {
            newRow[key.toLowerCase().trim()] = row[key];
          });
          return newRow;
        }).filter(row => row.bxid || row.id);

        if (cleanedData.length === 0) {
          throw new Error("Fisierul Excel nu contine date valide. Asigura-te ca exista coloana BXID (sau ID).");
        }

        const endpoint = activeTab === "mf" ? "/imports/mijloace-fixe-initiale" : "/imports/linii-mijloace-fixe-initiale";

        const res = await authFetch(endpoint, {
          method: "POST",
          body: JSON.stringify({ data: cleanedData })
        });

        if (res.result === "pass") {
          setSuccess(res.message || "Importul mijloacelor fixe a fost realizat cu succes!");
          setFile(null);
          setFileName("");
        } else {
          setError(res.message || "Importul a esuat.");
        }
      } catch (err) {
        setError(err.message || "Eroare la procesarea fisierului Excel.");
      } finally {
        setLoading(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  return (
    <Card className="shadow-lg border-0 rounded-4 overflow-hidden mb-4 bg-white">
      <div className="bg-gradient bg-primary p-4 text-white">
        <h3 className="mb-1 fw-bold">Import Mijloace Fixe</h3>
        <p className="mb-0 text-white-50 small">Preluarea mijloacelor fixe de amortizat și a istoricului de amortizare.</p>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => {
          setActiveTab(k);
          setError("");
          setSuccess("");
          setFile(null);
          setFileName("");
        }}
        className="bg-light border-bottom px-4 pt-2"
      >
        <Tab eventKey="mf" title="Mijloace Fixe (Fisa de Mijloc Fix)" />
        <Tab eventKey="linii" title="Linii Amortizare (Istoric)" />
      </Tabs>

      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="rounded-3">{error}</Alert>}
        {success && <Alert variant="success" className="rounded-3">{success}</Alert>}

        <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="fw-bold text-dark mb-1">Model Excel</h5>
            <p className="text-muted small mb-0">Descarcă modelul corespunzător secțiunii curente.</p>
          </div>
          <Button variant="outline-primary" onClick={downloadTemplate} className="rounded-pill px-4">
            Descarcă model Excel
          </Button>
        </div>

        <hr className="my-4 text-black-50" />

        <Form.Group className="mb-4">
          <Form.Label className="fw-bold text-dark mb-2">Selectare fișier import</Form.Label>
          <div className="border border-2 border-dashed border-primary rounded-3 p-4 text-center cursor-pointer position-relative">
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
              className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
            />
            <div className="py-2">
              <i className="bi bi-file-earmark-excel text-primary display-4 mb-2"></i>
              <p className="fw-semibold text-secondary mb-1">
                {fileName || "Trage fișierul aici sau fă click pentru a-l selecta"}
              </p>
              <p className="text-muted small mb-0">Sunt acceptate doar fișiere Excel (.xlsx, .xls)</p>
            </div>
          </div>
        </Form.Group>

        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={loading || !file}
            className="rounded-pill px-4 py-2 fw-semibold d-flex align-items-center"
          >
            {loading && <Spinner size="sm" animation="border" className="me-2" />}
            {loading ? "Se importă..." : "Efectuează Import"}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
